import type { Session } from '@sentra/contracts';
import { sessionSchema } from '@sentra/contracts';

import { apiRoutes } from '@/api/routes';

/**
 * Where the session this terminal may already hold is read from.
 *
 * From the route table, for the reason `CONFIG_PATH` gives: this module cannot use the client, but
 * a second copy of the path would sit outside the drift test.
 */
const SESSION_PATH = apiRoutes.authSession.path;

/**
 * What a reload learned about the session this browser holds.
 *
 * **Three answers, because "nobody is signed in" and "that could not be asked" lead to different
 * screens.** They used to be one: any answer but a valid session was `null`, so a reload during a
 * brief rate limit — or while the api was restarting — put a staff member who WAS signed in in
 * front of the sign-in form. The password they then typed was spent against the same limit, and the
 * gate terminal looked signed out for no reason anybody could see.
 */
export type InitialSession =
  | { readonly kind: 'session'; readonly session: Session }
  | { readonly kind: 'signed_out' }
  | { readonly kind: 'unavailable'; readonly retryAfterSeconds: number | null };

/**
 * The statuses that mean "ask again shortly" rather than "nobody is signed in".
 *
 * A rate limit and the three statuses a proxy answers for an api that is absent, restarting or too
 * slow. A plain 500 is deliberately NOT here: one that keeps recurring would otherwise trap the
 * terminal on a retry screen with no way to the sign-in form, and signing in again is the honest
 * way out of a session the server cannot read.
 */
const TRANSIENT_STATUSES: ReadonlySet<number> = new Set([429, 502, 503, 504]);

function retryAfterOf(response: Response): number | null {
  const seconds = Number.parseInt(response.headers.get('Retry-After') ?? '', 10);
  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

/**
 * The session this browser already has, whether it has none, or that it could not be asked.
 *
 * **It runs before the first render, beside the configuration, and not from a hook.** A reload in
 * the middle of a shift must not flash the sign-in screen: an effect that fetched the session after
 * mounting would paint the login form, then replace it, which reads as having been signed out and
 * is exactly the moment somebody types their password into a terminal that did not need it.
 *
 * **A 401 is the ordinary signed-out case, and so is a body that does not parse** — a client and a
 * server that have drifted, which must not be treated as a session. A request that never reached
 * the server, and a status in {@link TRANSIENT_STATUSES}, are `unavailable`.
 *
 * It uses a plain `fetch` rather than the API client for the reason `loadAppConfig` does: the client
 * is built inside the session provider, so nothing that runs before the provider exists can use it.
 */
export async function loadInitialSession(
  fetchImpl: typeof globalThis.fetch = globalThis.fetch,
): Promise<InitialSession> {
  let response: Response;
  try {
    response = await fetchImpl(SESSION_PATH, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
  } catch {
    return { kind: 'unavailable', retryAfterSeconds: null };
  }
  if (TRANSIENT_STATUSES.has(response.status)) {
    return { kind: 'unavailable', retryAfterSeconds: retryAfterOf(response) };
  }
  if (!response.ok) {
    return { kind: 'signed_out' };
  }
  try {
    const parsed = sessionSchema.safeParse(await response.json());
    return parsed.success ? { kind: 'session', session: parsed.data } : { kind: 'signed_out' };
  } catch {
    return { kind: 'signed_out' };
  }
}
